/**
 * CLIENT.TS - Repositorio: Architecture
 * Orquestador de Interfaz, Gestión de Identidad y Ruteo para Google Sites.
 */

import { ApiResponse, Profile, Sidehuman, AccountType } from '../types/shared-types';
import imageCompression from 'browser-image-compression';

export class HumanRespondsClient {
  private currentUser: Profile | null = null;
  private readonly WORKER_URL = "https://platform.human-responds.workers.dev";
  private mainCanvas: HTMLElement | null = null;

  constructor() {
    this.mainCanvas = document.getElementById('app-canvas');
    this.initRouter();
  }

  /**
   * 1. ROUTER DE NAVEGACIÓN
   */
  private initRouter(): void {
    const path = window.location.pathname;
    if (path.includes('/signup')) this.renderSignUp();
    else if (path.includes('/authenticity')) this.renderAuthenticityFlow();
    else if (path.includes('/dashboard')) this.renderDashboard();
    else if (path.includes('/create')) this.renderCreateSidehuman();
    else if (path.includes('/train')) this.renderTrainSidehuman();
    else if (path.includes('/market')) this.renderMarketplace();
    else if (path.includes('/legal')) this.renderLegalContact();
    else this.renderSignUp();
  }

  /**
   * 2.1 PÁGINA: CREATE A SIDEHUMAN
   */
  public renderCreateSidehuman(): void {
    if (!this.mainCanvas) return;
    const p = this.currentUser;

    if (p?.account_type === 'corporate') {
      this.mainCanvas.innerHTML = `<div class="sidehuman-card"><h3>No tiene permisos para crear un sidehuman.</h3></div>`;
      return;
    }
    
    if (p?.status !== 'Active') {
       this.mainCanvas.innerHTML = `<div class="sidehuman-card"><h3>Por favor complete el proceso de autenticación primero.</h3></div>`;
       return;
    }

    this.mainCanvas.innerHTML = `
      <div class="view-section">
        <div class="sidehuman-card" style="text-align: left;">
          <h2>Crear Sidehuman</h2>
          <form id="create-sh-form">
            <label>Nombre del Sidehuman</label><input type="text" id="sh-name" required>
            <label>Objetivo</label><textarea id="sh-obj" maxlength="200" required></textarea>
            <label>Ubicación Geográfica (Mapa)</label>
            <div id="map-mock" style="height:150px; background:#e0e0e0; border-radius:12px; display:flex; align-items:center; justify-content:center; color:#666; margin-bottom:10px;">
              [Mapa Interactivo - Punto basado en IP]
            </div>
            <label>Categoría</label>
            <select id="sh-cat">
              ${p?.account_type === 'professional' ? `
                <optgroup label="Professional Mode">
                  <option value="sociology">Sociología</option>
                </optgroup>
                <optgroup label="Normal Mode">
                  <option value="general">General</option>
                </optgroup>
              ` : '<option value="general">General</option>'}
            </select>
            <label>Comando de Entrenamiento (Ensayar conocimiento)</label>
            <p style="font-size:0.7rem; color:red;">* Prohibido copiar y pegar. Mínimo 2900 caracteres.</p>
            <textarea id="sh-training" style="height:250px;" required></textarea>
            <div id="char-counter" style="text-align:right; font-size:0.8rem; color:var(--medium-gray);">0 / 3000</div>
            <button type="submit" id="btn-submit-sh" class="btn-primary" style="width:100%; margin-top:15px;" disabled>Listo para entrenar</button>
          </form>
        </div>
      </div>
    `;

    const trainingTxt = document.getElementById('sh-training') as HTMLTextAreaElement;
    const btnSubmit = document.getElementById('btn-submit-sh') as HTMLButtonElement;
    const counter = document.getElementById('char-counter') as HTMLElement;

    trainingTxt.addEventListener('paste', (e) => {
      e.preventDefault();
      alert("Acción no permitida: Debe redactar el conocimiento manualmente.");
    });

    trainingTxt.addEventListener('input', () => {
      const len = trainingTxt.value.length;
      counter.innerText = `${len} / 3000`;
      btnSubmit.disabled = len < 2900;
      counter.style.color = len >= 2900 ? 'green' : 'red';
    });
  }

  /**
   * 2.2 PÁGINA: TRAIN A SIDEHUMAN
   */
  public renderTrainSidehuman(): void {
    if (!this.mainCanvas) return;

    this.mainCanvas.innerHTML = `
      <div class="view-section">
        <div class="sidehuman-card">
          <label>Sidehuman Seleccionado</label>
          <select id="select-sh-train"><option>Seleccione un Sidehuman...</option></select>
        </div>
        <div id="train-chat-container" class="sidehuman-card" style="text-align:left;">
          <div id="chat-box" style="height:300px; overflow-y:auto; background:var(--light-gray); padding:15px; border-radius:12px; margin-bottom:15px; border:1px solid var(--border-color);">
            <p style="color:var(--medium-gray); font-size:0.9rem;">Sistema: Inicie la conversación para entrenar a su Sidehuman.</p>
          </div>
          <label>Tu instrucción (250 - 300 palabras)</label>
          <textarea id="train-input" style="height:100px;"></textarea>
          <div id="word-counter" style="text-align:right; font-size:0.8rem;">Palabras: 0</div>
          <div style="display:flex; gap:10px; margin-top:10px;">
            <button class="btn-primary" id="btn-send-train" style="flex:1;">Enviar</button>
            <button class="btn-primary" id="btn-publish" style="flex:1; background:var(--success); display:none;">Publicar en Market</button>
          </div>
          <p id="last-update" style="font-size:0.7rem; margin-top:10px; color:var(--medium-gray);">Última actualización: Nunca</p>
        </div>
      </div>
    `;

    const input = document.getElementById('train-input') as HTMLTextAreaElement;
    const wordCounter = document.getElementById('word-counter') as HTMLElement;
    input.addEventListener('paste', (e) => e.preventDefault());
    input.addEventListener('input', () => {
      const words = input.value.trim().split(/\s+/).filter(w => w.length > 0).length;
      wordCounter.innerText = `Palabras: ${words} (Min 250 / Max 300)`;
      wordCounter.style.color = (words >= 250 && words <= 300) ? 'green' : 'red';
    });
  }

  /**
   * 3. PÁGINA: MARKETPLACE (Geográfico Estilo Waze)
   */
  public renderMarketplace(): void {
    if (!this.mainCanvas) return;
    this.mainCanvas.innerHTML = `
      <div class="view-section">
        <div class="sidehuman-card">
          <h2>Marketplace Global</h2>
          <p style="color: var(--medium-gray);">Sidehumans verificados en su ubicación.</p>
        </div>
        <div id="market-map-container" style="height: 500px; width: 100%; background: #f0f0f0; border-radius: 16px; position: relative; border: 1px solid var(--border-color); overflow: hidden;">
          <div class="sh-pin-wrapper" style="position: absolute; top: 40%; left: 30%;">
            <div class="sh-marker"></div>
            <div class="sh-popup">
              <h4>Experto en Leyes</h4>
              <p>Objetivo: Consultoría legal civil.</p>
              <p><strong>$10.00 / 1k palabras</strong></p>
              <button class="btn-primary" style="padding: 5px 10px; font-size: 0.7rem; width:100%;" onclick="window.client.buy('sh_1')">Rentar</button>
            </div>
          </div>
        </div>
      </div>
    `;
    this.injectMarketStyles();
  }

  /**
   * 4. PÁGINA: LEGAL & CONTACTO (Responsabilidad Cero)
   */
  public renderLegalContact(): void {
    if (!this.mainCanvas) return;
    this.mainCanvas.innerHTML = `
      <div class="view-section">
        <div class="sidehuman-card" style="text-align: left;">
          <h2 style="color: var(--primary-green);">Protocolo de Soberanía</h2>
          <div class="legal-box" style="background: var(--light-gray); padding: 20px; border-radius: 12px; border-left: 4px solid var(--primary-green); margin-bottom: 25px;">
            <h4>Responsabilidad Cero (0)</h4>
            <p style="font-size: 0.8rem; line-height: 1.6;">
              Toda interacción generada por un <strong>Sidehuman</strong> es responsabilidad absoluta del humano que lo entrenó. 
              La plataforma no tiene acceso a las llaves privadas ni al contenido del archivo <code>.sidehuman</code>.
            </p>
          </div>
          <h3>Contacto y Soporte</h3>
          <p style="font-size: 0.85rem;">admin@humanresponds.com | legal@humanresponds.com</p>
          <button class="btn-primary" style="margin-top:20px; width:100%; background: var(--dark-gray);" onclick="window.open('/terms.html', '_blank')">Ver Términos Completos</button>
        </div>
      </div>
    `;
  }

  private injectMarketStyles(): void {
    const styleId = 'market-css';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .sh-pin-wrapper { cursor: pointer; }
      .sh-marker { width: 20px; height: 20px; background: var(--primary-green); border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; transition: 0.3s; }
      .sh-popup { display: none; position: absolute; bottom: 30px; left: -90px; width: 200px; background: white; padding: 15px; border-radius: 12px; box-shadow: 0 4px 15px rgba(0,0,0,0.1); border: 1px solid var(--border-color); z-index: 10; }
      .sh-pin-wrapper:hover .sh-popup { display: block; }
    `;
    document.head.appendChild(style);
  }

  public buy(id: string): void { alert(`Iniciando pago para: ${id}`); }

  // --- MÉTODOS DE SOPORTE PRESERVADOS ---
  private renderSignUp(): void { /* Lógica de UI para registro */ }
  private renderAuthenticityFlow(): void { /* Lógica de UI para validación de ID */ }
  private renderDashboard(): void { /* Lógica de UI para los 7 recuadros */ }
  private getTermsText(): string { return "Términos de servicio de Human Responds 2026..."; }
}

(window as any).client = new HumanRespondsClient();
